// Pure helpers for the form's "Details as of …" line. `now_ms` is a parameter so
// the form's existing per-second tick is the only clock.

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
// IST has no DST, so a fixed offset is exact and avoids Intl locale variance.
const IST_OFFSET_MS = 330 * MINUTE_MS;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface DetailsAge {
  absolute: string;
  relative: string;
}

function parse_ms(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || !iso.trim()) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

// "17 Oct, 2:05 PM IST"; "" for missing/invalid input.
export function details_absolute_label(iso: string | null | undefined): string {
  const ms = parse_ms(iso);
  if (ms === null) return '';
  const d = new Date(ms + IST_OFFSET_MS);
  const h24 = d.getUTCHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ampm = h24 < 12 ? 'AM' : 'PM';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}, ${h12}:${mm} ${ampm} IST`;
}

// "just now" / "12 min ago" / "3 h 12 min ago" / "2 d 4 h ago"; "" for missing/invalid input.
export function details_relative_label(
  iso: string | null | undefined,
  now_ms: number | null | undefined
): string {
  const ms = parse_ms(iso);
  if (ms === null || typeof now_ms !== 'number' || !Number.isFinite(now_ms)) return '';
  // A future stamp (client clock behind the server) reads as fresh.
  const diff = Math.max(0, now_ms - ms);
  if (diff < MINUTE_MS) return 'just now';
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)} min ago`;
  if (diff < DAY_MS) {
    const h = Math.floor(diff / HOUR_MS);
    const m = Math.floor((diff % HOUR_MS) / MINUTE_MS);
    return m ? `${h} h ${m} min ago` : `${h} h ago`;
  }
  const d = Math.floor(diff / DAY_MS);
  const h = Math.floor((diff % DAY_MS) / HOUR_MS);
  return h ? `${d} d ${h} h ago` : `${d} d ago`;
}

// Both labels, or null when there is no usable stamp (the line is hidden).
export function details_age(
  iso: string | null | undefined,
  now_ms: number | null | undefined
): DetailsAge | null {
  const absolute = details_absolute_label(iso);
  if (!absolute) return null;
  return { absolute, relative: details_relative_label(iso, now_ms) };
}
